interface KioskSection {
    id: number;
    type?: string;
    subtitle: string;
    content: string;
}

interface ParseResult {
    sections: KioskSection[];
    firstImageUrl: string | null;
}

export function parsePageToKioskSections(html: string): ParseResult {
    if (!html) return { sections: [], firstImageUrl: null };

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const sections: KioskSection[] = [];
    let firstImageUrl: string | null = null;
    let idCounter = Date.now();

    // 태그를 그룹 키로 분류
    const getGroupKey = (tagName: string): string | null => {
        if (["H1", "H2", "H3"].includes(tagName)) return "heading";
        if (tagName === "P") return "p";
        if (["UL", "OL"].includes(tagName)) return "list";
        return null;
    };

    // 노드에서 텍스트 추출
    const extractText = (el: HTMLElement, tagName: string): string => {
        if (["UL", "OL"].includes(tagName)) {
            return Array.from(el.querySelectorAll("li"))
                .map(li => li.innerText?.trim() || li.textContent?.trim() || "")
                .filter(Boolean)
                .join("\n");
        }
        // br 태그를 \n으로 변환
        el.querySelectorAll("br").forEach(br => br.replaceWith("\n"));
        return el.innerText?.trim() || el.textContent?.trim() || "";
    };

    // 이미지 추출
    const extractImage = (el: HTMLElement) => {
        if (!firstImageUrl) {
            const img = el.tagName === "IMG" ? el as HTMLImageElement : el.querySelector?.("img");
            if (img) firstImageUrl = (img as HTMLImageElement).src;
        }
    };

    let currentGroupKey: string | null = null;
    let currentContent: string[] = [];

    const flushGroup = () => {
        if (currentContent.length > 0) {
            sections.push({
                id: idCounter++,
                subtitle: "",
                content: currentContent.join("\n")
            });
            currentContent = [];
        }
        currentGroupKey = null;
    };

    doc.body.childNodes.forEach(node => {
        const el = node as HTMLElement;
        const tagName = node.nodeName.toUpperCase();

        // 텍스트 노드 무시
        if (node.nodeType === Node.TEXT_NODE) return;

        // 이미지 체크
        extractImage(el);

        const groupKey = getGroupKey(tagName);

        if (!groupKey) {
            // 모르는 태그면 자식 순회
            flushGroup();
            return;
        }

        const text = extractText(el, tagName);
        if (!text) return;

        if (groupKey === currentGroupKey) {
            // 같은 태그 연속 → 합치기
            currentContent.push(text);
        } else {
            // 태그 바뀜 → 이전 그룹 flush 후 새 그룹 시작
            flushGroup();
            currentGroupKey = groupKey;
            currentContent.push(text);
        }
    });

    // 마지막 그룹 flush
    flushGroup();

    // 빈 섹션 제거
    const filtered = sections.filter(s => s.content.trim());

    if (filtered.length === 0) {
        filtered.push({ id: idCounter++, subtitle: "", content: "" });
    }

    return { sections: filtered, firstImageUrl };
}